import { Link } from "react-router";
import type { ProviderApprovalStatus } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { formatProviderReviewNote } from "./provider-review-note";
import { formatLabel, getApprovalTone } from "./provider-workspace-helpers";

export function ProviderApprovalBlockedPage({
  editRegistrationHref,
  isRefreshing,
  onRefresh,
  onSignOut,
  reviewNote,
  status,
  summary,
  userLabel,
}: {
  editRegistrationHref?: string;
  isRefreshing: boolean;
  onRefresh: () => Promise<void>;
  onSignOut: () => Promise<void>;
  reviewNote: string | null | undefined;
  status: ProviderApprovalStatus;
  summary: {
    checklist: string[];
    description: string;
    provider_name?: string | null;
    title: string;
  };
  userLabel: string;
}) {
  const { t } = useAppPreferences();

  return (
    <div className="shell-grid min-h-screen px-4 py-6 sm:px-6 sm:py-8 text-slate-100">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <div className="glass-panel rounded-[32px] border border-white/10 p-5 sm:p-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <p className="ops-kicker">{t("Provider onboarding")}</p>
              <h1 className="mt-3 break-words text-3xl font-semibold tracking-tight text-white sm:text-5xl">
                {summary.provider_name ?? summary.title}
              </h1>
              <p className="mt-4 ops-copy">{t(summary.description)}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="ops-chip" data-tone={getApprovalTone(status)}>
                  {formatLabel(status, t)}
                </span>
              </div>
            </div>

            <div className="min-w-0 lg:min-w-[260px] rounded-[24px] border border-white/10 bg-white/5 p-5 text-sm text-slate-200">
              <p className="ops-label">{t("Signed in as")}</p>
              <p className="mt-2 text-base font-medium text-white">{userLabel}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {editRegistrationHref ? (
                  <Link className="ops-button flex-1" data-variant="secondary" to={editRegistrationHref}>
                    {t("Edit registration")}
                  </Link>
                ) : null}
                <button
                  className="ops-button flex-1"
                  data-variant="secondary"
                  disabled={isRefreshing}
                  onClick={() => void onRefresh()}
                  type="button"
                >
                  {isRefreshing ? t("Refreshing...") : t("Refresh status")}
                </button>
                <button
                  className="ops-button flex-1"
                  data-variant="secondary"
                  onClick={() => void onSignOut()}
                  type="button"
                >
                  {t("common.signOut")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="glass-panel rounded-[28px] border border-white/10 p-6">
            <p className="ops-kicker">{t("Next steps")}</p>
            <div className="mt-5 grid gap-3">
              {summary.checklist.map((item) => (
                <div className="ops-record text-sm leading-7 text-slate-300" key={item}>
                  {t(item)}
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-[28px] border border-white/10 p-6">
            <p className="ops-kicker">{t("Admin note")}</p>
            <div className="mt-5 whitespace-pre-wrap break-words rounded-[22px] border border-white/10 bg-slate-950/35 px-5 py-4 text-sm leading-7 text-slate-300">
              {reviewNote ? formatProviderReviewNote(reviewNote, t) : t("No admin note yet. Refresh this page after gaamed reviews the registration.")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
